import React, { useEffect, useRef, useState } from 'react';

import type {
  ApiClient
} from '../api/client';
import type {
  CodexRun,
  QueueActivityEvent,
  RunActivityTree,
  RunCommandOutputLine,
  RunCommandRawOutputLine
} from '../types';
import { mergeActivityTree } from '../utils/conversationRunActivity';

const QUEUE_ACTIVITY_POLL_MS = 5000;
const QUEUE_ACTIVITY_POLL_HIDDEN_MS = 30000;

export type RunnerSidebarDragState = {
  pointerId: number;
  startX: number;
  startWidth: number;
  minWidth: number;
  maxWidth: number;
};

type Args = {
  apiClient: ApiClient;
  conversationId: string;
  canLoadRuntime: boolean;
  shellAutoExpandMessageId: string | null;
  messages: Array<{ id: string }>;
  setShellAutoExpandMessageId: (value: string | null) => void;
  setError: (message: string | null) => void;
  reportErrorLocal: (
    context: string,
    error: unknown,
    setError: (message: string | null) => void,
    setLocalError: (message: string | null) => void
  ) => void;
  runnerSidebarWidthStorageKey: string;
  mobileLayoutQuery: string;
  runnerSidebarMinWidth: number;
  runnerThreadMinWidth: number;
  runnerSidebarDefaultWidthRatio: number;
  runnerSidebarDefaultWidthFallback: number;
  activityStagesPage: number;
  activityStageCommandsPage: number;
  activityOutputLinesPage: number;
};

function queueActivityEventsEqual(
  left: QueueActivityEvent[] | undefined,
  right: QueueActivityEvent[] | undefined
) {
  if (left === right) {
    return true;
  }
  if (!left || !right || left.length !== right.length) {
    return false;
  }
  for (let i = 0; i < left.length; i += 1) {
    if (
      left[i].id !== right[i].id ||
      left[i].event_name !== right[i].event_name ||
      left[i].created_at !== right[i].created_at ||
      left[i].message !== right[i].message ||
      left[i].phase !== right[i].phase ||
      left[i].stage !== right[i].stage ||
      left[i].error !== right[i].error
    ) {
      return false;
    }
  }
  return true;
}

function pageIsVisible() {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

export function queueActivityIsTerminal(events: QueueActivityEvent[] | undefined) {
  const latestEvent = events?.[events.length - 1];
  return Boolean(
    latestEvent &&
      ['queue.finished', 'queue.failed', 'queue.skipped', 'queue.canceled'].includes(
        latestEvent.event_name
      )
  );
}

export function useConversationActivitySidebar({
  apiClient,
  conversationId,
  canLoadRuntime,
  shellAutoExpandMessageId,
  messages,
  setShellAutoExpandMessageId,
  setError,
  reportErrorLocal,
  runnerSidebarWidthStorageKey,
  mobileLayoutQuery,
  runnerSidebarMinWidth,
  runnerThreadMinWidth,
  runnerSidebarDefaultWidthRatio,
  runnerSidebarDefaultWidthFallback,
  activityStagesPage,
  activityStageCommandsPage,
  activityOutputLinesPage
}: Args) {
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [showRunnerSidebar, setShowRunnerSidebar] = useState(false);
  const [runnerSidebarWidth, setRunnerSidebarWidth] = useState<number | null>(null);
  const [isMobileLayout, setIsMobileLayout] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia(mobileLayoutQuery).matches
  );
  const [selectedActivityMessageId, setSelectedActivityMessageId] = useState<string | null>(null);
  const [queueActivityByMessageId, setQueueActivityByMessageId] = useState<
    Record<string, QueueActivityEvent[]>
  >({});
  const [queueActivityLoadingByMessageId, setQueueActivityLoadingByMessageId] = useState<
    Record<string, boolean>
  >({});
  const [activityTreeByMessageId, setActivityTreeByMessageId] = useState<
    Record<string, RunActivityTree>
  >({});
  const [activityTreeLoadingByMessageId, setActivityTreeLoadingByMessageId] = useState<
    Record<string, boolean>
  >({});
  const [expandedActivityStages, setExpandedActivityStages] = useState<Record<string, boolean>>({});
  const [expandedActivityCommands, setExpandedActivityCommands] = useState<Record<string, boolean>>(
    {}
  );
  const [visibleActivityStagesByMessage, setVisibleActivityStagesByMessage] = useState<
    Record<string, number>
  >({});
  const [visibleActivityCommandsByStage, setVisibleActivityCommandsByStage] = useState<
    Record<string, number>
  >({});
  const [visibleActivityOutputLinesByCommand, setVisibleActivityOutputLinesByCommand] = useState<
    Record<string, number>
  >({});
  const [loadingOlderActivityByMessageId, setLoadingOlderActivityByMessageId] = useState<
    Record<string, boolean>
  >({});
  const [loadingOlderCommandOutput, setLoadingOlderCommandOutput] = useState<
    Record<string, boolean>
  >({});
  const [copyingRawCommandOutput, setCopyingRawCommandOutput] = useState<Record<string, boolean>>(
    {}
  );
  const effectiveShowRunnerSidebar = canLoadRuntime && showRunnerSidebar;
  const runnerSidebarWidthRef = useRef<number | null>(null);
  const runnerSidebarDragStateRef = useRef<RunnerSidebarDragState | null>(null);
  const conversationBodyRef = useRef<HTMLDivElement | null>(null);
  const loadQueueActivityRef = useRef(loadQueueActivity);
  const queueActivityInFlightRef = useRef<Set<string>>(new Set());
  const queueActivityByMessageIdRef = useRef(queueActivityByMessageId);

  const stageExpandKey = (messageId: string, stageId: string) => `${messageId}::stage::${stageId}`;
  const commandExpandKey = (messageId: string, commandId: string) =>
    `${messageId}::command::${commandId}`;
  const messageVisibleStageCountKey = (messageId: string) => `${messageId}::stages-visible`;
  const stageVisibleCommandCountKey = (messageId: string, stageId: string) =>
    `${messageId}::stage-visible::${stageId}`;
  const commandVisibleOutputLineCountKey = (messageId: string, commandId: string) =>
    `${messageId}::command-output-visible::${commandId}`;

  useEffect(() => {
    setEventsError(null);
    setShowRunnerSidebar(false);
    setSelectedActivityMessageId(null);
    setQueueActivityByMessageId({});
    setQueueActivityLoadingByMessageId({});
    setActivityTreeByMessageId({});
    setActivityTreeLoadingByMessageId({});
    setExpandedActivityStages({});
    setExpandedActivityCommands({});
    setVisibleActivityStagesByMessage({});
    setVisibleActivityCommandsByStage({});
    setVisibleActivityOutputLinesByCommand({});
    setLoadingOlderActivityByMessageId({});
    setLoadingOlderCommandOutput({});
    setCopyingRawCommandOutput({});
  }, [conversationId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const media = window.matchMedia(mobileLayoutQuery);
    const handleChange = () => setIsMobileLayout(media.matches);
    handleChange();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, [mobileLayoutQuery]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const raw = window.localStorage.getItem(runnerSidebarWidthStorageKey);
    if (!raw) {
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return;
    }
    runnerSidebarWidthRef.current = parsed;
    setRunnerSidebarWidth(parsed);
  }, [runnerSidebarWidthStorageKey]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    return () => {
      document.body.classList.remove('runner-sidebar-resizing');
    };
  }, []);

  useEffect(() => {
    if (isMobileLayout || typeof window === 'undefined') {
      return;
    }
    const onResize = () => {
      const current = runnerSidebarWidthRef.current;
      if (current === null) {
        return;
      }
      const containerWidth = conversationBodyRef.current?.getBoundingClientRect().width ?? 0;
      if (containerWidth <= 0) {
        return;
      }
      const maxWidth = Math.max(runnerSidebarMinWidth, containerWidth - runnerThreadMinWidth);
      const clamped = Math.min(maxWidth, Math.max(runnerSidebarMinWidth, current));
      if (Math.abs(clamped - current) < 0.5) {
        return;
      }
      runnerSidebarWidthRef.current = clamped;
      setRunnerSidebarWidth(clamped);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMobileLayout, runnerSidebarMinWidth, runnerThreadMinWidth]);

  function getRunnerSidebarWidthBounds() {
    const containerWidth = conversationBodyRef.current?.getBoundingClientRect().width ?? 0;
    const minWidth = runnerSidebarMinWidth;
    const maxWidth =
      containerWidth > 0
        ? Math.max(minWidth, containerWidth - runnerThreadMinWidth)
        : runnerSidebarDefaultWidthFallback;
    return { minWidth, maxWidth };
  }

  function clampRunnerSidebarWidth(width: number) {
    const { minWidth, maxWidth } = getRunnerSidebarWidthBounds();
    return Math.min(maxWidth, Math.max(minWidth, width));
  }

  function resolveCurrentRunnerSidebarWidth() {
    if (runnerSidebarWidthRef.current !== null) {
      return runnerSidebarWidthRef.current;
    }
    const sidebarWidth =
      conversationBodyRef.current
        ?.querySelector<HTMLElement>('.runner-sidebar')
        ?.getBoundingClientRect().width ?? null;
    if (sidebarWidth !== null) {
      return sidebarWidth;
    }
    const containerWidth = conversationBodyRef.current?.getBoundingClientRect().width ?? 0;
    if (containerWidth > 0) {
      return containerWidth * runnerSidebarDefaultWidthRatio;
    }
    return runnerSidebarDefaultWidthFallback;
  }

  function resizeRunnerSidebarByDelta(delta: number) {
    if (isMobileLayout || !showRunnerSidebar) {
      return;
    }
    const base = clampRunnerSidebarWidth(resolveCurrentRunnerSidebarWidth());
    const next = clampRunnerSidebarWidth(base + delta);
    runnerSidebarWidthRef.current = next;
    setRunnerSidebarWidth(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(runnerSidebarWidthStorageKey, String(Math.round(next)));
    }
  }

  function startRunnerSidebarResize(event: React.PointerEvent<HTMLDivElement>) {
    if (isMobileLayout || !conversationBodyRef.current) {
      return;
    }
    const { minWidth, maxWidth } = getRunnerSidebarWidthBounds();
    const startWidth = clampRunnerSidebarWidth(resolveCurrentRunnerSidebarWidth());
    runnerSidebarDragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth,
      minWidth,
      maxWidth
    };
    runnerSidebarWidthRef.current = startWidth;
    setRunnerSidebarWidth(startWidth);
    event.currentTarget.setPointerCapture(event.pointerId);
    document.body.classList.add('runner-sidebar-resizing');
    event.preventDefault();
  }

  function onRunnerSidebarResize(event: React.PointerEvent<HTMLDivElement>) {
    const drag = runnerSidebarDragStateRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    const deltaX = event.clientX - drag.startX;
    const nextWidth = Math.min(drag.maxWidth, Math.max(drag.minWidth, drag.startWidth - deltaX));
    runnerSidebarWidthRef.current = nextWidth;
    setRunnerSidebarWidth(nextWidth);
  }

  function finishRunnerSidebarResize(event: React.PointerEvent<HTMLDivElement>) {
    const drag = runnerSidebarDragStateRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    runnerSidebarDragStateRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    document.body.classList.remove('runner-sidebar-resizing');
    const width = runnerSidebarWidthRef.current;
    if (typeof window !== 'undefined' && width !== null) {
      window.localStorage.setItem(runnerSidebarWidthStorageKey, String(Math.round(width)));
    }
  }

  function handleRunnerSidebarResizeKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      resizeRunnerSidebarByDelta(24);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      resizeRunnerSidebarByDelta(-24);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      const next = getRunnerSidebarWidthBounds().minWidth;
      runnerSidebarWidthRef.current = next;
      setRunnerSidebarWidth(next);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(runnerSidebarWidthStorageKey, String(Math.round(next)));
      }
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      const next = getRunnerSidebarWidthBounds().maxWidth;
      runnerSidebarWidthRef.current = next;
      setRunnerSidebarWidth(next);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(runnerSidebarWidthStorageKey, String(Math.round(next)));
      }
    }
  }

  async function loadQueueActivity(messageId: string, options?: { silent?: boolean }) {
    if (!messageId || queueActivityInFlightRef.current.has(messageId)) {
      return undefined;
    }
    queueActivityInFlightRef.current.add(messageId);
    const shouldShowLoading = !options?.silent;
    if (shouldShowLoading) {
      setQueueActivityLoadingByMessageId((prev) => ({ ...prev, [messageId]: true }));
    }
    try {
      const data = await apiClient.get<QueueActivityEvent[]>(
        `/api/conversations/${conversationId}/queue-events/${encodeURIComponent(messageId)}`
      );
      setQueueActivityByMessageId((prev) => {
        const existing = prev[messageId];
        if (queueActivityEventsEqual(existing, data)) {
          return prev;
        }
        return { ...prev, [messageId]: data };
      });
      return data;
    } catch (err) {
      reportErrorLocal('Failed to load queue activity', err, setError, setEventsError);
      return undefined;
    } finally {
      queueActivityInFlightRef.current.delete(messageId);
      if (shouldShowLoading) {
        setQueueActivityLoadingByMessageId((prev) => ({ ...prev, [messageId]: false }));
      }
    }
  }

  loadQueueActivityRef.current = loadQueueActivity;
  queueActivityByMessageIdRef.current = queueActivityByMessageId;

  async function loadRunActivityTree(
    messageId: string,
    runId: string,
    force = false,
    beforeEventId?: number | null
  ) {
    const isPagedRequest = typeof beforeEventId === 'number';
    if (!force && !isPagedRequest && activityTreeByMessageId[messageId]) {
      return;
    }
    if (activityTreeLoadingByMessageId[messageId] || loadingOlderActivityByMessageId[messageId]) {
      return;
    }
    if (isPagedRequest) {
      setLoadingOlderActivityByMessageId((prev) => ({ ...prev, [messageId]: true }));
    } else {
      setActivityTreeLoadingByMessageId((prev) => ({ ...prev, [messageId]: true }));
    }
    try {
      const params = new URLSearchParams();
      params.set('command_limit', '240');
      params.set('output_tail_limit', '16');
      if (isPagedRequest) {
        params.set('before_event_id', String(beforeEventId));
      }
      const data = await apiClient.get<RunActivityTree>(
        `/api/conversations/${conversationId}/runs/${runId}/activity-tree?${params.toString()}`
      );
      setActivityTreeByMessageId((prev) => {
        if (!isPagedRequest || force || !prev[messageId]) {
          return { ...prev, [messageId]: data };
        }
        return { ...prev, [messageId]: mergeActivityTree(prev[messageId], data) };
      });
      setVisibleActivityStagesByMessage((prev) => {
        const key = messageVisibleStageCountKey(messageId);
        const current = prev[key] ?? 0;
        const next = Math.max(current, Math.min(data.stages.length, activityStagesPage));
        return { ...prev, [key]: next };
      });
      setVisibleActivityCommandsByStage((prev) => {
        const next = { ...prev };
        for (const stage of data.stages) {
          const key = stageVisibleCommandCountKey(messageId, stage.id);
          const current = next[key] ?? 0;
          const target = Math.min(stage.commands.length, activityStageCommandsPage);
          next[key] = Math.max(current, target);
        }
        return next;
      });
    } catch (err) {
      reportErrorLocal('Failed to load activity tree', err, setError, setEventsError);
    } finally {
      if (isPagedRequest) {
        setLoadingOlderActivityByMessageId((prev) => ({ ...prev, [messageId]: false }));
      } else {
        setActivityTreeLoadingByMessageId((prev) => ({ ...prev, [messageId]: false }));
      }
    }
  }

  async function loadOlderActivityCommands(messageId: string, runId: string) {
    const tree = activityTreeByMessageId[messageId];
    if (!tree || !tree.has_more_commands || !tree.next_before_event_id) {
      return;
    }
    if (loadingOlderActivityByMessageId[messageId]) {
      return;
    }
    await loadRunActivityTree(messageId, runId, false, tree.next_before_event_id);
  }

  function ensureRunnerSidebarVisible(messageId?: string | null) {
    if (messageId) {
      setSelectedActivityMessageId(messageId);
    }
    setShowRunnerSidebar(true);
  }

  function closeRunnerSidebar() {
    runnerSidebarDragStateRef.current = null;
    document.body.classList.remove('runner-sidebar-resizing');
    setShowRunnerSidebar(false);
  }

  function toggleExpandedActivityStage(stageKey: string, isExpanded: boolean) {
    setExpandedActivityStages((prev) => ({ ...prev, [stageKey]: !isExpanded }));
  }

  function toggleExpandedActivityCommand(
    commandKey: string,
    isExpanded: boolean,
    outputVisibleKey: string,
    loadedOutputCount: number
  ) {
    setExpandedActivityCommands((prev) => ({ ...prev, [commandKey]: !isExpanded }));
    if (!isExpanded) {
      setVisibleActivityOutputLinesByCommand((prev) => ({
        ...prev,
        [outputVisibleKey]: Math.max(
          prev[outputVisibleKey] ?? 0,
          Math.min(loadedOutputCount, activityOutputLinesPage)
        )
      }));
    }
  }

  function showMoreActivityStages(stageVisibleCountKey: string, stageCount: number) {
    setVisibleActivityStagesByMessage((prev) => ({
      ...prev,
      [stageVisibleCountKey]: Math.min(
        stageCount,
        (prev[stageVisibleCountKey] ?? activityStagesPage) + activityStagesPage
      )
    }));
  }

  function showMoreActivityCommands(stageVisibleCommandCountKey: string, commandCount: number) {
    setVisibleActivityCommandsByStage((prev) => ({
      ...prev,
      [stageVisibleCommandCountKey]: Math.min(
        commandCount,
        (prev[stageVisibleCommandCountKey] ?? activityStageCommandsPage) +
          activityStageCommandsPage
      )
    }));
  }

  function showMoreLoadedActivityOutput(outputVisibleKey: string, loadedOutputCount: number) {
    setVisibleActivityOutputLinesByCommand((prev) => ({
      ...prev,
      [outputVisibleKey]: Math.min(
        loadedOutputCount,
        (prev[outputVisibleKey] ?? activityOutputLinesPage) + activityOutputLinesPage
      )
    }));
  }

  async function openMessageActivitySidebar(messageId: string, runId?: string | null) {
    ensureRunnerSidebarVisible(messageId);
    if (runId) {
      await loadRunActivityTree(messageId, runId);
    }
  }

  async function loadOlderCommandOutput(messageId: string, runId: string, commandId: string) {
    const tree = activityTreeByMessageId[messageId];
    if (!tree) {
      return;
    }
    const command = tree.stages.flatMap((stage) => stage.commands).find((value) => value.id === commandId);
    if (!command || !command.has_more_output) {
      return;
    }
    const loadingKey = commandExpandKey(messageId, commandId);
    if (loadingOlderCommandOutput[loadingKey]) {
      return;
    }
    const oldest = command.output_tail[0]?.id;
    if (!oldest) {
      return;
    }
    setLoadingOlderCommandOutput((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const params = new URLSearchParams();
      params.set('before', String(oldest));
      params.set('limit', '200');
      const data = await apiClient.get<RunCommandOutputLine[]>(
        `/api/conversations/${conversationId}/runs/${runId}/commands/${encodeURIComponent(commandId)}/output?${params.toString()}`
      );
      setActivityTreeByMessageId((prev) => {
        const current = prev[messageId];
        if (!current) {
          return prev;
        }
        const stages = current.stages.map((stage) => ({
          ...stage,
          commands: stage.commands.map((candidate) => {
            if (candidate.id !== commandId) {
              return candidate;
            }
            const merged = [...data, ...candidate.output_tail];
            const deduped: RunCommandOutputLine[] = [];
            const seen = new Set<number>();
            for (const line of merged) {
              if (seen.has(line.id)) {
                continue;
              }
              seen.add(line.id);
              deduped.push(line);
            }
            return {
              ...candidate,
              output_tail: deduped,
              has_more_output: data.length >= 200
            };
          })
        }));
        return { ...prev, [messageId]: { ...current, stages } };
      });
    } catch (err) {
      reportErrorLocal('Failed to load older command output', err, setError, setEventsError);
    } finally {
      setLoadingOlderCommandOutput((prev) => ({ ...prev, [loadingKey]: false }));
    }
  }

  async function copyRawAgentCommandOutput(messageId: string, runId: string, commandId: string) {
    const copyKey = commandExpandKey(messageId, commandId);
    if (copyingRawCommandOutput[copyKey]) {
      return;
    }
    setCopyingRawCommandOutput((prev) => ({ ...prev, [copyKey]: true }));
    try {
      const params = new URLSearchParams();
      params.set('limit', '5000');
      const data = await apiClient.get<RunCommandRawOutputLine[]>(
        `/api/conversations/${conversationId}/runs/${runId}/commands/${encodeURIComponent(commandId)}/raw-output?${params.toString()}`
      );
      if (data.length === 0) {
        throw new Error('No raw output available for this command');
      }
      const payloads = data.map((line) => line.payload.trim()).filter((line) => line.length > 0);
      const rawAgentJsonPayloads = payloads
        .map((line) => {
          const prefixed = line.match(/^\[[a-zA-Z0-9_.:-]+\]\s*agent container:\s*(.*)$/);
          return prefixed?.[1]?.trim() ?? line;
        })
        .filter((line) => line.startsWith('{') || line.startsWith('['));
      const rawText = (rawAgentJsonPayloads.length > 0 ? rawAgentJsonPayloads : payloads).join('\n');
      if (!rawText.trim()) {
        throw new Error('No raw output available for this command');
      }
      await navigator.clipboard.writeText(rawText);
    } catch (err) {
      reportErrorLocal('Failed to copy raw agent output', err, setError, setEventsError);
    } finally {
      setCopyingRawCommandOutput((prev) => ({ ...prev, [copyKey]: false }));
    }
  }

  useEffect(() => {
    if (!effectiveShowRunnerSidebar || !selectedActivityMessageId) {
      return;
    }
    let canceled = false;
    let timer: number | null = null;
    const poll = async () => {
      if (canceled) {
        return;
      }
      if (!pageIsVisible()) {
        timer = window.setTimeout(() => {
          void poll();
        }, QUEUE_ACTIVITY_POLL_HIDDEN_MS);
        return;
      }
      const data = await loadQueueActivityRef.current(selectedActivityMessageId, { silent: true });
      const cachedData = queueActivityByMessageIdRef.current[selectedActivityMessageId];
      if (canceled || queueActivityIsTerminal(data ?? cachedData)) {
        return;
      }
      timer = window.setTimeout(() => {
        void poll();
      }, QUEUE_ACTIVITY_POLL_MS);
    };
    void poll();
    return () => {
      canceled = true;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [effectiveShowRunnerSidebar, selectedActivityMessageId]);

  useEffect(() => {
    if (!canLoadRuntime || !shellAutoExpandMessageId) {
      return;
    }
    if (!messages.some((message) => message.id === shellAutoExpandMessageId)) {
      return;
    }
    setSelectedActivityMessageId(shellAutoExpandMessageId);
    setShowRunnerSidebar(true);
    setShellAutoExpandMessageId(null);
  }, [canLoadRuntime, messages, setShellAutoExpandMessageId, shellAutoExpandMessageId]);

  const conversationBodyClassName = [
    'conversation-body',
    effectiveShowRunnerSidebar ? 'conversation-body--split' : '',
    effectiveShowRunnerSidebar && isMobileLayout ? 'conversation-body--activity-focus' : ''
  ]
    .filter(Boolean)
    .join(' ');
  const conversationBodyStyle =
    effectiveShowRunnerSidebar && !isMobileLayout && runnerSidebarWidth !== null
      ? ({ '--runner-sidebar-width': `${Math.round(runnerSidebarWidth)}px` } as React.CSSProperties)
      : undefined;

  return {
    eventsError,
    setEventsError,
    showRunnerSidebar,
    setShowRunnerSidebar,
    runnerSidebarWidth,
    isMobileLayout,
    selectedActivityMessageId,
    setSelectedActivityMessageId,
    queueActivityByMessageId,
    queueActivityLoadingByMessageId,
    activityTreeByMessageId,
    activityTreeLoadingByMessageId,
    expandedActivityStages,
    expandedActivityCommands,
    visibleActivityStagesByMessage,
    visibleActivityCommandsByStage,
    visibleActivityOutputLinesByCommand,
    loadingOlderActivityByMessageId,
    loadingOlderCommandOutput,
    copyingRawCommandOutput,
    conversationBodyRef,
    conversationBodyClassName,
    conversationBodyStyle,
    startRunnerSidebarResize,
    onRunnerSidebarResize,
    finishRunnerSidebarResize,
    handleRunnerSidebarResizeKeyDown,
    toggleExpandedActivityStage,
    toggleExpandedActivityCommand,
    showMoreActivityStages,
    showMoreActivityCommands,
    showMoreLoadedActivityOutput,
    openMessageActivitySidebar,
    loadOlderActivityCommands,
    loadOlderCommandOutput,
    copyRawAgentCommandOutput,
    ensureRunnerSidebarVisible,
    closeRunnerSidebar,
    loadQueueActivity,
    loadRunActivityTree,
    messageVisibleStageCountKey,
    stageExpandKey,
    stageVisibleCommandCountKey,
    commandExpandKey,
    commandVisibleOutputLineCountKey
  };
}
